export type DefinitionRevisionChangeType = "added" | "removed" | "changed" | "created";

/** A presentation-neutral, semantic change between two domain revisions. */
export interface DefinitionRevisionChange {
  label: string;
  type: DefinitionRevisionChangeType;
  before?: string;
  after?: string;
}

const FIELD_LABELS: Record<string, string> = {
  title: "Title",
  name: "Name",
  description: "Description",
  durationMinutes: "Duration",
  status: "Status",
  availability: "Availability",
  procedureNoteLabel: "Procedure note label",
  procedureNoteField: "Procedure note",
  category: "Category",
  performerType: "Performer type",
  supportsConditions: "Supports conditions",
  schedulingParams: "Scheduling",
  scheduling: "Scheduling",
  observations: "Observations",
  observationSchema: "Observations",
  parameters: "Parameters",
  requirements: "Requirements",
  resourceRequirements: "Resource requirements",
  devices: "Devices",
  location: "Location",
  steps: "Steps",
  procedures: "Procedures",
  days: "Days",
  sessions: "Sessions",
  items: "Questions",
  sections: "Sections",
  findings: "Findings",
  components: "Components",
  options: "Options",
  responses: "Responses",
  required: "Required",
  repeatable: "Repeatable",
  repeats: "Allows multiple",
  allowsMultiple: "Allows multiple",
  isScored: "Scored",
  requiresDiagnosticEvidence: "Requires diagnostic evidence",
  timeWindow: "Time window",
  definitionCanonical: "Definition",
  definition: "Definition",
  code: "Code",
  label: "Label",
  text: "Text",
  type: "Type",
  value: "Value",
  min: "Minimum",
  max: "Maximum",
  step: "Step",
  unit: "Unit",
  cardinality: "Cardinality",
  allowFreeText: "Allows free text",
  maxLength: "Maximum length",
  readOnly: "Read only",
  enableWhen: "Display conditions",
  enableBehavior: "Condition behavior",
};

const IGNORED_FIELDS = new Set([
  "id",
  "linkId",
  "resourceId",
  "questionnaireId",
  "definitionId",
  "url",
  "version",
  "mintedAt",
  "updatedAt",
  "lastUpdated",
  "resourceType",
  "meta",
  "profiles",
  "catalog",
  // Derived display state, not definition-authored facts.
  "findingCount",
  "totalDurationMinutes",
  "isBookable",
  "unbookableReason",
  "protocolTitle",
  "activityDefinitionId",
  "definitionUrl",
  "definitionVersion",
]);

type UnknownRecord = Record<string, unknown>;

/** Compare two clean definition DTOs without exposing their FHIR representation. */
export function diffDefinitionRevisions(previous: unknown, current: unknown): DefinitionRevisionChange[] {
  const changes: DefinitionRevisionChange[] = [];
  diffValue(previous, current, [], changes);
  return changes;
}

function diffValue(previous: unknown, current: unknown, path: string[], changes: DefinitionRevisionChange[]): void {
  if (equivalent(previous, current)) return;

  if (Array.isArray(previous) && Array.isArray(current)) {
    diffArray(previous, current, path, changes);
    return;
  }

  if (isRecord(previous) && isRecord(current)) {
    for (const key of new Set([...Object.keys(previous), ...Object.keys(current)])) {
      if (IGNORED_FIELDS.has(key)) continue;
      if (key === "extension") {
        if (!equivalent(previous[key], current[key])) {
          changes.push({ label: pathLabel([...path, "Configuration"]), type: "changed" });
        }
        continue;
      }
      diffValue(previous[key], current[key], [...path, fieldLabel(key)], changes);
    }
    return;
  }

  const label = pathLabel(path);
  if (previous === undefined || previous === null || previous === "") {
    changes.push({ label, type: "added", after: formatValue(current, path) });
  } else if (current === undefined || current === null || current === "") {
    changes.push({ label, type: "removed", before: formatValue(previous, path) });
  } else {
    changes.push({
      label,
      type: "changed",
      before: formatValue(previous, path),
      after: formatValue(current, path),
    });
  }
}

function diffArray(previous: unknown[], current: unknown[], path: string[], changes: DefinitionRevisionChange[]): void {
  if (previous.every(isRecord) && current.every(isRecord)) {
    const previousByKey = keyed(previous);
    const currentByKey = keyed(current);
    const keys = new Set([...previousByKey.keys(), ...currentByKey.keys()]);

    if (
      previousByKey.size === currentByKey.size &&
      [...previousByKey.keys()].every((key) => currentByKey.has(key)) &&
      [...previousByKey.keys()].join("|") !== [...currentByKey.keys()].join("|")
    ) {
      changes.push({ label: `${pathLabel(path)} order`, type: "changed" });
    }

    for (const key of keys) {
      const before = previousByKey.get(key);
      const after = currentByKey.get(key);
      const item = after ?? before;
      const itemPath = [...path, itemLabel(item, key)];
      if (!before) {
        changes.push({ label: pathLabel(itemPath), type: "added" });
      } else if (!after) {
        changes.push({ label: pathLabel(itemPath), type: "removed" });
      } else {
        diffValue(before, after, itemPath, changes);
      }
    }
    return;
  }

  const beforeValues = new Set(previous.map(stableString));
  const afterValues = new Set(current.map(stableString));
  for (const item of current) {
    if (!beforeValues.has(stableString(item))) {
      changes.push({ label: pathLabel([...path, formatValue(item, path)]), type: "added" });
    }
  }
  for (const item of previous) {
    if (!afterValues.has(stableString(item))) {
      changes.push({ label: pathLabel([...path, formatValue(item, path)]), type: "removed" });
    }
  }
}

function keyed(items: UnknownRecord[]): Map<string, UnknownRecord> {
  const seen = new Map<string, number>();
  return new Map(
    items.map((item, index) => {
      const base = itemIdentity(item) ?? `item-${index}`;
      const occurrence = seen.get(base) ?? 0;
      seen.set(base, occurrence + 1);
      return [`${base}:${occurrence}`, item];
    }),
  );
}

function itemIdentity(item: UnknownRecord): string | undefined {
  for (const key of ["id", "linkId", "name", "code", "definitionCanonical", "definition", "title", "label", "text"]) {
    const value = item[key];
    if (typeof value === "string" && value) return `${key}:${value}`;
  }
  return undefined;
}

function itemLabel(item: UnknownRecord | undefined, fallback: string): string {
  if (!item) return fallback;
  for (const key of ["label", "title", "text", "name", "code"]) {
    const value = item[key];
    if (typeof value === "string" && value) return value;
  }
  for (const key of ["definitionCanonical", "definition"]) {
    const value = item[key];
    if (typeof value === "string" && value) return value.split("/").at(-1) ?? value;
  }
  return fallback.replace(/:\d+$/, "");
}

function fieldLabel(key: string): string {
  return FIELD_LABELS[key] ?? key.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/^./, (letter) => letter.toUpperCase());
}

function pathLabel(path: string[]): string {
  return path.filter(Boolean).join(" › ") || "Definition";
}

function formatValue(value: unknown, path: string[]): string {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number" && path.at(-1) === "Duration") return `${value} min`;
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (value === undefined || value === null) return "Not set";
  return "Updated";
}

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function equivalent(left: unknown, right: unknown): boolean {
  return stableString(left) === stableString(right);
}

function stableString(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableString).join(",")}]`;
  if (isRecord(value)) {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${key}:${stableString(value[key])}`)
      .join(",")}}`;
  }
  return JSON.stringify(value) ?? String(value);
}
